import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';
import { mapVoiceSessionToConversation } from './conversations';
import type { ConversationSession } from './conversations';

// ── Outbound Scheduler ──────────────────────────────────────────────────────

export interface OutboundContact {
  phone: string;
  name?: string;
}

export interface OutboundCampaignRow {
  id: string;
  campaign_id: string;
  campaign_name: string;
  status: 'scheduled' | 'running' | 'paused' | 'completed' | 'cancelled';
  drop_datetime: string;
  timezone: string;
  contacts_total: number;
  // Dial attempts, not unique contacts — increments once per retry, so this
  // can exceed contacts_total for a contact that needed more than one try.
  // Use contacts_dialed_unique for any "X / total" or percentage display.
  contacts_dialed: number;
  // Optional: absent on responses from a backend deployed before this field
  // was added. Callers fall back to contacts_dialed in that case.
  contacts_dialed_unique?: number;
  contacts_connected: number;
  concurrency_limit: number;
  max_attempts_per_contact: number;
  min_retry_gap_hours: number;
}

export interface OutboundContactRow {
  id: string;
  phone: string;
  name: string;
  status: 'pending' | 'dialing' | 'connected' | 'completed' | 'failed' | 'skipped_dnc' | 'skipped_optout';
  attempts: number;
  outcome: string;
  session_id: string | null;
  last_attempt_at: string | null;
}

export async function createOutboundCampaign(
  data: {
    campaign_id: string;
    drop_datetime: string;
    timezone: string;
    concurrency_limit?: number;
    max_attempts_per_contact?: number;
    min_retry_gap_hours?: number;
    contacts: OutboundContact[];
  },
  axiosInstance?: AxiosInstance
): Promise<OutboundCampaignRow> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<OutboundCampaignRow>('/api/outbound/campaigns', data);
  return response.data;
}

export async function fetchOutboundCampaigns(
  params?: { status?: string; limit?: number },
  axiosInstance?: AxiosInstance
): Promise<OutboundCampaignRow[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<OutboundCampaignRow[]>('/api/outbound/campaigns', { params });
  return response.data;
}

export async function fetchOutboundCampaign(
  id: string,
  axiosInstance?: AxiosInstance
): Promise<OutboundCampaignRow> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<OutboundCampaignRow>(`/api/outbound/campaigns/${id}`);
  return response.data;
}

export async function controlOutboundCampaign(
  id: string,
  action: 'pause' | 'resume' | 'cancel',
  axiosInstance?: AxiosInstance
): Promise<void> {
  const client = axiosInstance || getVoiceFallbackClient();
  await client.post(`/api/outbound/campaigns/${id}/${action}`);
}

export async function fetchOutboundContacts(
  id: string,
  params?: { status?: string; limit?: number },
  axiosInstance?: AxiosInstance
): Promise<OutboundContactRow[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<OutboundContactRow[]>(`/api/outbound/campaigns/${id}/contacts`, { params });
  return response.data;
}

export async function fetchVoiceCampaignList(
  axiosInstance?: AxiosInstance
): Promise<{ id: string; name: string }[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>('/api/campaigns');
  const rows = response.data?.campaigns ?? response.data ?? [];
  return rows.map((r: any) => ({ id: r.id ?? r.campaign_id, name: r.name ?? r.campaign_name ?? r.id }));
}

export async function fetchConversations(
  axiosInstance?: AxiosInstance,
  params?: { start_date?: string; end_date?: string; campaign_id?: string; outcome?: string }
): Promise<ConversationSession[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.voice}/sessions`;
  const cleanParams = params ? Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')) : undefined;
  const response = await client.get<unknown>(url, cleanParams && Object.keys(cleanParams).length > 0 ? { params: cleanParams } : undefined);
  const raw = response.data as any;
  const list = Array.isArray(raw) ? raw : raw?.sessions ?? raw?.items ?? raw?.data ?? [];
  return (list as any[]).map(mapVoiceSessionToConversation);
}

/**
 * Same endpoint as {@link fetchConversations}, but also surfaces the real
 * `total` count the backend already computes — GET /api/voice/sessions
 * supports limit/offset, but nothing before this used them, so the Sessions
 * tab silently only ever showed the first page with no way to see more.
 */
export async function fetchConversationsPage(
  axiosInstance?: AxiosInstance,
  params?: { start_date?: string; end_date?: string; campaign_id?: string; outcome?: string; limit?: number; offset?: number }
): Promise<{ sessions: ConversationSession[]; total: number }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.voice}/sessions`;
  const cleanParams = params ? Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')) : undefined;
  const response = await client.get<unknown>(url, cleanParams && Object.keys(cleanParams).length > 0 ? { params: cleanParams } : undefined);
  const raw = response.data as any;
  const list = Array.isArray(raw) ? raw : raw?.sessions ?? raw?.items ?? raw?.data ?? [];
  const total = Array.isArray(raw) ? list.length : Number(raw?.total ?? list.length);
  return { sessions: (list as any[]).map(mapVoiceSessionToConversation), total };
}

/**
 * `GET /api/voice/sessions` with optional query params (e.g. `campaign_id`).
 * Use this for campaign-scoped session lists; {@link fetchConversations} remains unfiltered for backwards compatibility.
 */
export async function fetchVoiceSessionsList(
  params: { campaign_id?: string; limit?: number } = {},
  axiosInstance?: AxiosInstance,
): Promise<ConversationSession[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.voice}/sessions`;
  const response = await client.get<unknown>(url, { params });
  const raw = response.data as any;
  const list = Array.isArray(raw) ? raw : raw?.sessions ?? raw?.items ?? raw?.data ?? [];
  return (list as any[]).map(mapVoiceSessionToConversation);
}

/** `DELETE /api/voice/session/{session_id}` — delete a single session. */
export async function deleteVoiceSession(sessionId: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.voice}/session/${sessionId}`;
  const response = await client.delete(url);
  return response.status === 200 || response.status === 204;
}

/** `POST /api/voice/sessions/bulk-delete` — delete multiple sessions. */
export async function bulkDeleteVoiceSessions(sessionIds: string[], axiosInstance?: AxiosInstance): Promise<{ deleted: number }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.voice}/sessions/bulk-delete`;
  const response = await client.post<{ deleted: number }>(url, { session_ids: sessionIds });
  return response.data;
}
